import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Save, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() || '';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const WhatsAppServiceUrlConfig = () => {
  const [url, setUrl] = useState('');
  const [savedUrl, setSavedUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Charger la config
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await authFetch(`${API}/api/config/whatsapp-service-url`);
      const data = await response.json();
      if (data.url) {
        setUrl(data.url);
        setSavedUrl(data.url);
      }
    } catch (err) {
      console.error('Erreur chargement config WhatsApp URL:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/api/config/whatsapp-service-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      const data = await response.json();
      if (data.success) {
        setSavedUrl(url);
        setTestResult({ success: true, message: 'URL sauvegardée !' });
      } else {
        setTestResult({ success: false, message: data.error || 'Erreur sauvegarde' });
      }
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await authFetch(`${API}/api/whatsapp/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url || savedUrl })
      });
      const data = await response.json();
      setTestResult({
        success: data.success,
        message: data.message || (data.success ? 'Connexion OK !' : 'Échec connexion'),
        details: data.details
      });
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="text-slate-400 text-sm">Chargement...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="http://localhost:3001 ou URL externe"
          className="flex-1 bg-slate-900/50 border-slate-600"
        />
        <Button 
          onClick={saveConfig} 
          disabled={saving || url === savedUrl}
          className="bg-green-600 hover:bg-green-700"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </Button>
        <Button 
          onClick={testConnection} 
          disabled={testing}
          variant="outline"
        >
          {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Tester'}
        </Button>
      </div>

      {savedUrl && (
        <div className="text-xs text-slate-500">
          <span className="text-slate-400">URL configurée :</span> {savedUrl}
        </div>
      )}

      {testResult && (
        <div className={`p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
          <div className="flex items-center gap-2">
            {testResult.success ? (
              <CheckCircle className="w-4 h-4 text-green-400" />
            ) : (
              <XCircle className="w-4 h-4 text-red-400" />
            )}
            <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
              {testResult.message}
            </span>
          </div>
          {testResult.details && (
            <pre className="mt-2 text-xs text-slate-400 overflow-x-auto">
              {JSON.stringify(testResult.details, null, 2)}
            </pre>
          )}
        </div>
      )}

      <div className="text-xs text-slate-500 bg-slate-900/50 rounded-lg p-3">
        <p className="mb-1"><strong>Exemples d'URL :</strong></p>
        <ul className="list-disc list-inside space-y-1">
          <li><code>http://localhost:3001</code> - Service local</li>
          <li><code>https://votre-domaine.com/whatsapp</code> - Service externe</li>
        </ul>
      </div>
    </div>
  );
};

export default WhatsAppServiceUrlConfig;
